import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, linkedSignal, output, signal } from '@angular/core';

/**
 * Material Pagination Component 🔥
 * @description Pagination component following OB1 design pattern
 * Manages page navigation with configurable page size
 */
@Component({
    selector: 'ecm-mat-pagination',
    templateUrl: 'material-pagination.component.html',
    standalone: true,
    imports: [CommonModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MaterialPaginationComponent {
    // ═══════════════════════════════════════════════════════════════
    // 📥 INPUTS
    // ═══════════════════════════════════════════════════════════════

    /**
     * Total number of items
     */
    totalItems = input.required<number>();

    /**
     * Number of items per page
     */
    pageSize = input(10);

    /**
     * Current page (1-indexed)
     */
    currentPage = input(1);

    /**
     * Show page numbers or just next/previous buttons
     */
    showPageNumbers = input(true);

    /**
     * Show page counter (e.g., "Page 1 of 10")
     */
    showCounter = input(false);

    /**
     * Number of page buttons to show around current page
     */
    siblingCount = input(1);

    /**
     * Test ID for e2e testing
     */
    testId = input('e2e-mat-pagination');

    /**
     * Show navigation text (Précédent/Suivant) on buttons
     */
    showNavigationText = input(false);

    // ═══════════════════════════════════════════════════════════════
    // 📤 OUTPUTS
    // ═══════════════════════════════════════════════════════════════

    /**
     * Emitted when page changes
     */
    pageChange = output<number>();

    /**
     * Emitted when page size changes
     */
    pageSizeChange = output<number>();

    // ═══════════════════════════════════════════════════════════════
    // 🔗 INTERNAL STATE SIGNALS
    // ═══════════════════════════════════════════════════════════════

    /**
     * Internal mutable state for page size
     */
    private readonly pageSizeState = signal(10);

    // ═══════════════════════════════════════════════════════════════
    // 🔗 COMPUTED SIGNALS
    // ═══════════════════════════════════════════════════════════════

    /**
     * Current page signal getter
     */
    public currentPageSignal = linkedSignal(() => this.currentPage());

    /**
     * Total number of pages
     */
    public totalPages = computed(() => {
        const size = this.pageSize();
        return Math.ceil(Math.max(0, this.totalItems()) / size);
    });

    /**
     * Whether we can go to previous page
     */
    public canGoPrevious = computed(() => this.currentPageSignal() > 1);

    /**
     * Whether we can go to next page
     */
    public canGoNext = computed(() => this.currentPageSignal() < this.totalPages());

    /**
     * Array of page numbers to display
     */
    public paginationRange = computed(() => {
        const totalPages = this.totalPages();
        const currentPage = this.currentPageSignal();
        const siblings = this.siblingCount();

        if (totalPages <= 1) return [];

        // Calculate range
        const leftSibling = Math.max(currentPage - siblings, 1);
        const rightSibling = Math.min(currentPage + siblings, totalPages);

        // Build page array
        const pages: (number | string)[] = [];

        // Add first page
        if (leftSibling > 1) {
            pages.push(1);
            if (leftSibling > 2) {
                pages.push('...');
            }
        }

        // Add middle pages
        for (let i = leftSibling; i <= rightSibling; i++) {
            pages.push(i);
        }

        // Add last page
        if (rightSibling < totalPages) {
            if (rightSibling < totalPages - 1) {
                pages.push('...');
            }
            pages.push(totalPages);
        }

        return pages;
    });

    /**
     * Counter text (e.g., "Page 1 of 10")
     */
    public counterText = computed(() => {
        return `Page ${this.currentPageSignal()} of ${this.totalPages()}`;
    });

    // ═══════════════════════════════════════════════════════════════
    // 🎯 PUBLIC METHODS
    // ═══════════════════════════════════════════════════════════════

    /**
     * Go to specific page
     * @param page Page number to go to (1-indexed)
     */
    public goToPage(page: number | string): void {
        if (typeof page === 'string') return;

        const validPage = Math.max(1, Math.min(page, this.totalPages()));
        if (validPage !== this.currentPageSignal()) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            this.pageChange.emit(validPage);
        }
    }

    /**
     * Go to previous page
     */
    public previousPage(): void {
        if (this.canGoPrevious()) {
            this.goToPage(this.currentPageSignal() - 1);
        }
    }

    /**
     * Go to next page
     */
    public nextPage(): void {
        if (this.canGoNext()) {
            this.goToPage(this.currentPageSignal() + 1);
        }
    }

    /**
     * Update page size
     * @param newSize New page size
     */
    public updatePageSize(newSize: number): void {
        if (newSize > 0 && newSize !== this.pageSize()) {
            this.pageSizeState.set(newSize);
            // Reset to first page when changing page size
            // this.currentPageState.set(1);
            this.pageSizeChange.emit(newSize);
            this.pageChange.emit(1);
        }
    }

    /**
     * Check if page number is current page
     * @param page Page number to check
     */
    public isCurrentPage(page: number | string): boolean {
        return typeof page === 'number' && page === this.currentPageSignal();
    }

    /**
     * Check if page number is ellipsis
     * @param page Value to check
     */
    public isEllipsis(page: number | string): boolean {
        return page === '...';
    }
}
